import React, { useMemo } from 'react'
import { useFormik } from 'formik'
import { Button, Grid, CircularProgress, FormControlLabel, Switch } from '@mui/material'
import { Input } from '@fullscreendigital/carrefour-dex-fe'
import { Modal } from 'components/core-ui/Modal'
import { useNotification } from 'hooks'
import { useCreateManagerMutation, useManagerQuery, useUpdateManagerMutation } from '../../api'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { EditManagerModalProps } from '../../interfaces'
import { managersValidationSchema } from './helpers'

const EditManagerModal: React.FC<EditManagerModalProps> = ({
  isModalOpen = false,
  onModalClose,
  managerId,
  onSuccess,
  selectManager
}) => {
  const { showNotification } = useNotification()

  const shouldRefreshData = useMemo(() => isModalOpen === true, [isModalOpen])

  const { data: manager, isLoading } = useManagerQuery(
    showNotification,
    managerId,
    shouldRefreshData
  )

  const updateManagerMutation = useUpdateManagerMutation(showNotification, () => {
    formik.setFieldValue('isEnabled', manager?.isEnabled ?? true)
  })
  const createManagerMutation = useCreateManagerMutation(showNotification)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: manager?.id || null,
      firstName: manager?.firstName || '',
      lastName: manager?.lastName || '',
      email: manager?.email || '',
      phone: manager?.phone || '',
      isEnabled: manager?.isEnabled ?? true
    },
    validationSchema: managersValidationSchema,
    onSubmit: async values => {
      let response
      if (!managerId) {
        response = await createManagerMutation.mutateAsync(values)
      } else {
        values.id = managerId
        response = await updateManagerMutation.mutateAsync({ managerId, updatedData: values })
      }
      onModalClose()
      onSuccess()
      selectManager && selectManager(response)
      formik.resetForm()
    }
  })

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={onModalClose}
      modalTitle={managerId ? 'Edit manager' : 'Create manager'}
      modalWidth="50%"
    >
      <Grid container spacing={2}>
        {isLoading || updateManagerMutation.isLoading ? (
          <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
        ) : (
          <>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="firstName"
                label="First Name"
                width="100%"
                disabled={!!managerId}
                value={formik.values.firstName}
                required
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="lastName"
                label="Last Name"
                width="100%"
                disabled={!!managerId}
                value={formik.values.lastName}
                required
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="email"
                label="Email"
                width="100%"
                value={formik.values.email}
                required
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <Input
                type="text"
                variant="outlined"
                name="phone"
                label="Phone"
                width="100%"
                value={formik.values.phone}
                required
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
            </Grid>
            {managerId && (
              <Grid item xs={12}>
                <span style={{ display: 'inline-block', lineHeight: '38px', marginRight: '12px' }}>
                  Status:
                </span>
                <FormControlLabel
                  control={
                    <Switch
                      name="isEnabled"
                      checked={formik.values.isEnabled}
                      onChange={event => formik.setFieldValue('isEnabled', event.target.checked)}
                      onBlur={formik.handleBlur}
                    />
                  }
                  label="Enabled"
                />
                <span
                  style={{
                    color: 'rgba(0, 0, 0, 0.38)',
                    display: 'inline-block',
                    lineHeight: '38px'
                  }}
                >
                  (can be disabled if no active assigned partners)
                </span>
              </Grid>
            )}
            <Grid item xs={12} className={s.modalButtons}>
              <Button variant="outlined" onClick={onModalClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => formik.handleSubmit()}
                disabled={formik.isSubmitting}
              >
                Save
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </Modal>
  )
}

export default EditManagerModal
